import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Play, Image as ImageIcon, Video, User } from 'lucide-react';

interface MediaItem {
  id: string;
  type: 'image' | 'video';
  url: string;
  thumbnail?: string;
  alt?: string;
  reviewId: string;
  userName: string;
}

interface MediaGalleryProps {
  media: MediaItem[];
  onClose: () => void;
}

const MediaGallery: React.FC<MediaGalleryProps> = ({ media, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filter, setFilter] = useState<'all' | 'image' | 'video'>('all');

  const filteredMedia = media.filter(item => {
    if (filter === 'all') return true;
    return item.type === filter;
  });

  const currentItem = filteredMedia[currentIndex];

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % filteredMedia.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + filteredMedia.length) % filteredMedia.length);
  };

  const goToItem = (index: number) => {
    setCurrentIndex(index);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') goToPrev();
    if (e.key === 'ArrowRight') goToNext();
  };

  if (filteredMedia.length === 0) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Заголовок и фильтры */}
      <div className="bg-black bg-opacity-50">
        {/* Мобильная версия */}
        <div className="md:hidden">
          <div className="flex items-center justify-between p-3">
            <h3 className="text-white text-base font-semibold">
              Фото и видео
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300 transition-colors duration-200 p-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex items-center justify-center space-x-2 pb-3 px-3">
            <button
              onClick={() => setFilter('all')}
              className={`px-2 py-1 rounded text-xs transition-colors duration-200 ${
                filter === 'all'
                  ? 'bg-white text-black'
                  : 'text-white hover:bg-white hover:bg-opacity-20'
              }`}
            >
              Все ({media.length})
            </button>
            <button
              onClick={() => setFilter('image')}
              className={`px-2 py-1 rounded text-xs transition-colors duration-200 flex items-center space-x-1 ${
                filter === 'image'
                  ? 'bg-white text-black'
                  : 'text-white hover:bg-white hover:bg-opacity-20'
              }`}
            >
              <ImageIcon className="w-3 h-3" />
              <span>Фото ({media.filter(m => m.type === 'image').length})</span>
            </button>
            <button
              onClick={() => setFilter('video')}
              className={`px-2 py-1 rounded text-xs transition-colors duration-200 flex items-center space-x-1 ${
                filter === 'video'
                  ? 'bg-white text-black'
                  : 'text-white hover:bg-white hover:bg-opacity-20'
              }`}
            >
              <Video className="w-3 h-3" />
              <span>Видео ({media.filter(m => m.type === 'video').length})</span>
            </button>
          </div>
        </div>
        
        {/* Десктопная версия */}
        <div className="hidden md:flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <h3 className="text-white text-lg font-semibold">
              Фото и видео от покупателей
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 rounded text-sm transition-colors duration-200 ${
                  filter === 'all'
                    ? 'bg-white text-black'
                    : 'text-white hover:bg-white hover:bg-opacity-20'
                }`}
              >
                Все ({media.length})
              </button>
              <button
                onClick={() => setFilter('image')}
                className={`px-3 py-1 rounded text-sm transition-colors duration-200 flex items-center space-x-1 ${
                  filter === 'image'
                    ? 'bg-white text-black'
                    : 'text-white hover:bg-white hover:bg-opacity-20'
                }`}
              >
                <ImageIcon className="w-4 h-4" />
                <span>Фото ({media.filter(m => m.type === 'image').length})</span>
              </button>
              <button
                onClick={() => setFilter('video')}
                className={`px-3 py-1 rounded text-sm transition-colors duration-200 flex items-center space-x-1 ${
                  filter === 'video'
                    ? 'bg-white text-black'
                    : 'text-white hover:bg-white hover:bg-opacity-20'
                }`}
              >
                <Video className="w-4 h-4" />
                <span>Видео ({media.filter(m => m.type === 'video').length})</span>
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 transition-colors duration-200 p-2"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Основной контент */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="relative max-w-5xl max-h-full w-full">
          {/* Навигация */}
          {filteredMedia.length > 1 && (
            <>
              <button
                onClick={goToPrev}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10
                         p-3 bg-black bg-opacity-50 text-white rounded-full
                         hover:bg-opacity-70 transition-colors duration-200"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10
                         p-3 bg-black bg-opacity-50 text-white rounded-full
                         hover:bg-opacity-70 transition-colors duration-200"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Медиа */}
          <div className="flex items-center justify-center">
            {currentItem.type === 'video' ? (
              <video
                key={currentItem.id}
                src={currentItem.url}
                controls
                autoPlay
                className="max-w-full max-h-[70vh] rounded-lg"
                poster={currentItem.thumbnail}
              />
            ) : (
              <img
                key={currentItem.id}
                src={currentItem.url}
                alt={currentItem.alt || 'Изображение от покупателя'}
                className="max-w-full max-h-[70vh] rounded-lg object-contain"
              />
            )}
          </div>

          {/* Информация о медиа */}
          <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded-lg">
            <div className="flex items-center space-x-2 text-sm">
              <User className="w-4 h-4" />
              <span>{currentItem.userName}</span>
            </div>
          </div>

          {/* Счетчик */}
          {filteredMedia.length > 1 && (
            <div className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded-lg">
              <span className="text-sm">
                {currentIndex + 1} из {filteredMedia.length}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Миниатюры */}
      {filteredMedia.length > 1 && (
        <div className="p-4 bg-black bg-opacity-50">
          <div className="flex justify-center space-x-2 overflow-x-auto">
            {filteredMedia.map((item, index) => (
              <button
                key={item.id}
                onClick={() => goToItem(index)}
                className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                  index === currentIndex
                    ? 'border-white'
                    : 'border-transparent hover:border-gray-400'
                }`}
              >
                {item.type === 'video' ? (
                  <>
                    <video
                      src={item.url}
                      className="w-full h-full object-cover"
                      poster={item.thumbnail}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                      <Play className="w-4 h-4 text-white" />
                    </div>
                  </>
                ) : (
                  <img
                    src={item.url}
                    alt={item.alt || `Миниатюра ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
